import { createLogger } from "logging";
import { ConfirmDeleteTarget } from "models/ConfirmDeleteTarget";
import { ParticipantDetailModel } from "models/ParticipantDetailModel";
import { ToggleType } from "models/ToggleType";
import { useApi } from "providers";
import { useMeeting } from "providers";
import { useToasts } from "providers";
import { useTranslations } from "providers";
import React, { PropsWithChildren, startTransition, useCallback, useEffect, useMemo, useState } from "react";

import { ConfirmDeleteParticipant } from "./components/ConfirmDeleteParticipant";
import { ParticipantContext } from "./ParticipantContext";

const logger = createLogger("ParticipantProvider");

export const ParticipantProvider: React.FC<PropsWithChildren<unknown>> = ({ children }) => {
  const i18n = useTranslations();
  const toasts = useToasts();
  const { meeting, reloadMeeting } = useMeeting();
  const { participantsApi } = useApi();

  const [deleting, setDeleting] = useState<ToggleType>({});
  const [updating, setUpdating] = useState<ToggleType>({});

  const [confirmDeleteParticipant, setConfirmDeleteParticipant] = useState<ConfirmDeleteTarget | undefined>(undefined);
  const [selectParticipantId, setSelectParticipantId] = useState<number>(-1);
  const [participants, setParticipants] = useState<ParticipantDetailModel[]>();

  const handleSetParticipants = useCallback(
    (itemsToSet: ParticipantDetailModel[]) => {
      logger.trace("handleSetParticipants(): items:=", itemsToSet);
      setParticipants(itemsToSet);

      const newSelectedId = itemsToSet.find((x) => x.id === selectParticipantId)
        ? selectParticipantId
        : itemsToSet.length > 0
          ? itemsToSet[0].id
          : -1;
      setSelectParticipantId(newSelectedId);
      logger.debug("handleSetParticipants(): selected:=", newSelectedId);
    },
    [selectParticipantId]
  );

  const setParticipantUpdating = useCallback((id: number, newState: boolean) => {
    setUpdating((prev) => ({ ...prev, [id]: newState }));
  }, []);

  const isParticipantUpdating = useCallback(
    (id: number): boolean => {
      const flag = updating[id];
      if (flag !== undefined) return flag;
      return false;
    },
    [updating]
  );

  const allowDrag = useMemo(() => {
    return meeting?.canAddParticipant == true;
  }, [meeting]);

  const setParticipantDeleting = useCallback((id: number, newState: boolean) => {
    setDeleting((prev) => ({ ...prev, [id]: newState }));
  }, []);

  const isParticipantDeleting = useCallback(
    (id: number): boolean => {
      const flag = deleting[id];
      if (flag !== undefined) return flag;
      return false;
    },
    [deleting]
  );

  useEffect(() => {
    startTransition(() => {
      handleSetParticipants(meeting?.participants ?? []);
    });
  }, [meeting, handleSetParticipants]);

  const onParticipantSortEnd = useCallback(
    async (oldIndex: number, newIndex: number) => {
      logger.debug(`onSortEnd(): `, oldIndex, newIndex);
      if (!participants) throw new Error("Participants null");

      const participant = participants[oldIndex];
      const id = participant.id;
      const title = participant.displayName;
      setParticipantUpdating(id, true);

      try {
        await participantsApi.reorder(id, newIndex);
      } catch (error) {
        toasts.updateFailure(i18n.Participant, title, error);
      } finally {
        setParticipantUpdating(id, false);
      }

      reloadMeeting();
    },
    [participants, setParticipantUpdating, participantsApi, i18n, toasts, reloadMeeting]
  );

  const contextValue = useMemo(
    () => ({
      confirmDeleteParticipant,
      setConfirmDeleteParticipant,
      selectParticipantId,
      setSelectParticipantId,
      participants,
      setParticipants,
      isParticipantUpdating,
      isParticipantDeleting,
      setParticipantUpdating,
      setParticipantDeleting,
      onParticipantSortEnd,
      allowDrag,
    }),
    [
      confirmDeleteParticipant,
      setConfirmDeleteParticipant,
      selectParticipantId,
      setSelectParticipantId,
      participants,
      setParticipants,
      isParticipantUpdating,
      isParticipantDeleting,
      setParticipantUpdating,
      setParticipantDeleting,
      onParticipantSortEnd,
      allowDrag,
    ]
  );

  return (
    <ParticipantContext.Provider value={contextValue}>
      {children}
      <ConfirmDeleteParticipant />
    </ParticipantContext.Provider>
  );
};
